/**
 * Roles and permissions (docs/SECURITY.md §3, ADR-018). Roles are a fixed enum and the matrix is
 * code: a unit test compares it with the table in SECURITY.md, so the two cannot drift apart.
 * Browser-safe: the dashboard uses it only to hide navigation (never as a security control).
 */
export const ROLES = ['owner', 'admin', 'sales_head', 'marketing', 'salesperson'] as const;
export type Role = (typeof ROLES)[number];

export const PERMISSIONS = [
  'dashboard.overview',
  'dashboard.funnel',
  'dashboard.team',
  'dashboard.marketing',
  'dashboard.retention',
  'customers.view_names',
  'dashboard.tasks',
  'data_quality.view',
  'reports.view_history',
  'reports.configure',
  'mappings.manage',
  'integration.manage',
  'users.manage',
  'audit.view',
  'bot.commands',
  'bot.view_team',
  'bot.view_marketing',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/**
 * How far a role's permission reaches:
 * - `all`: everything,
 * - `own`: only data of the user's own salesperson (the API injects a scope into every query),
 * - `aggregates`: aggregate figures only, no row-level lists.
 */
export type Grant = 'all' | 'own' | 'aggregates';

const ALL: Grant = 'all';
const OWN: Grant = 'own';

// Owner and admin hold every permission (admins additionally cannot create or modify owners; that
// rule belongs to the user-management code, not to the matrix).
const EVERYTHING = Object.fromEntries(PERMISSIONS.map((p) => [p, ALL])) as Partial<
  Record<Permission, Grant>
>;

export const PERMISSION_MATRIX: Readonly<
  Record<Role, Readonly<Partial<Record<Permission, Grant>>>>
> = {
  owner: EVERYTHING,
  admin: EVERYTHING,
  sales_head: {
    'dashboard.overview': ALL,
    'dashboard.funnel': ALL,
    'dashboard.team': ALL,
    'dashboard.marketing': ALL,
    'dashboard.retention': ALL,
    'customers.view_names': ALL,
    'dashboard.tasks': ALL,
    'data_quality.view': ALL,
    'reports.view_history': ALL,
    'bot.commands': ALL,
    'bot.view_team': ALL,
    'bot.view_marketing': ALL,
  },
  marketing: {
    'dashboard.overview': ALL,
    'dashboard.funnel': ALL,
    'dashboard.marketing': ALL,
    'dashboard.retention': 'aggregates',
    'bot.commands': ALL,
    'bot.view_marketing': ALL,
  },
  salesperson: {
    'dashboard.overview': OWN,
    'dashboard.funnel': OWN,
    'customers.view_names': OWN,
    'dashboard.tasks': OWN,
  },
};

export function grantFor(role: Role, permission: Permission): Grant | null {
  return PERMISSION_MATRIX[role][permission] ?? null;
}

export function can(role: Role, permission: Permission): boolean {
  return grantFor(role, permission) !== null;
}

export function permissionsOf(role: Role): Permission[] {
  return PERMISSIONS.filter((permission) => can(role, permission));
}

export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}
